import { Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import CoordinatesInformation from '@components/map/popup/common/CoordinatesInformation';
import PopupContainer from '@components/map/popup/common/PopupContainer';
import { DraggablePanel } from '@components/ui/draggable';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { selectHeightSuffix } from '@redux/settings/settings.selectors';
import { selectTargetById } from '@redux/situation/situation.selectors';
import { useRetrieveCoordinatesAltitudeQuery } from '@services/c2/c2.api';
import { getTargetAltitude, getTargetHeight, isSpatial } from '@utils/target.utils';
import { convertDistanceUnitFromMeter, HeightUnits, unitLabel } from '@utils/unit.utils';
import { toCoordinates } from '@utils/validation/coordinates.utils';
import { FormattedMessage } from 'react-intl';
import QRCode from 'react-qr-code';

import { PopupComponentProps, PopupTypeEnum } from '@/types/popup.types';

export default function GpsQrCodePopup({
  mapConstraintsRef,
  data,
  top,
  left,
  onClose,
}: Readonly<PopupComponentProps<PopupTypeEnum.GPS_QR_CODE>>) {
  const { coordinates, targetId } = data;
  const qrCodesize = useBreakpointValue({ base: 212, '4k': 280 });
  const target = useSelectorWithReplayMode(selectTargetById, targetId);
  const showHeightSuffix = useAppSelector(selectHeightSuffix);
  const position = target?.lastPosition.location?.position ?? coordinates;
  const legalCoordinates = toCoordinates([position.longitude, position.latitude])!;
  const queryParameter = encodeURIComponent(`${legalCoordinates.latitude},${legalCoordinates.longitude}`);
  const { data: altitudes = [], isLoading } = useRetrieveCoordinatesAltitudeQuery({
    coordinates: [legalCoordinates],
    instanceId: 'QrCodePopup',
  });
  const altitude: number | undefined = altitudes.length >= 1 && !isLoading ? altitudes[0] : undefined;

  const altitudeLabel = (value: number | undefined | null) => {
    if (value === undefined || value === null) {
      return '-';
    }
    return `${Math.round(value)} ${unitLabel[HeightUnits.METERS]} (${convertDistanceUnitFromMeter(HeightUnits.FEET, value, 0)} ${unitLabel[HeightUnits.FEET]})`;
  };

  const targetAltitude = target ? getTargetAltitude(target) : null;
  const targetHeight = target ? getTargetHeight(target) : null;

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="components.popups.gpsQrCode.label" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={{ base: '260px', '4k': '344px' }}>
        <VStack gap={0.25}>
          <PopupContainer>
            <QRCode
              value={`https://www.google.com/maps/search/?api=1&query=${queryParameter}`}
              level="H"
              size={qrCodesize}
            />
          </PopupContainer>
          <CoordinatesInformation display="vertical" coordinates={legalCoordinates} />
          <PopupContainer>
            <VStack gap="tightL">
              <VStack gap="tightM" textStyle="screenStd">
                <Text color="neutral.300">
                  <FormattedMessage id="components.popups.gpsQrCode.groundAltitude" values={{ showHeightSuffix }} />
                </Text>
                <Text>{altitudeLabel(altitude)}</Text>
              </VStack>
              {target && isSpatial(target) && (
                <VStack gap="tightM" textStyle="screenStd">
                  {targetAltitude !== null || targetHeight === null ? (
                    <>
                      <Text color="neutral.300">
                        <FormattedMessage
                          id="components.popups.gpsQrCode.targetAltitude"
                          values={{ showHeightSuffix }}
                        />
                      </Text>
                      <Text>
                        {targetAltitude !== null ? altitudeLabel(targetAltitude) : <FormattedMessage id="global.NA" />}
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text color="neutral.300">
                        <FormattedMessage id="components.popups.gpsQrCode.targetHeight" values={{ showHeightSuffix }} />
                      </Text>
                      <Text>{altitudeLabel(targetHeight)}</Text>
                    </>
                  )}
                </VStack>
              )}
            </VStack>
          </PopupContainer>
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
